import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CreditCard, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function FlexiblePayment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [selectedMembershipFees, setSelectedMembershipFees] = useState(null);
  const [isMember, setIsMember] = useState(false);
  
  const [paymentData, setPaymentData] = useState({
    category: 'membership',
    subcategory: '',
    customAmount: '',
    purpose: '',
    name: '',
    email: '',
    phone: ''
  });

  // Load Razorpay script and payment options on component mount
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    script.onerror = () => {
      setError('Failed to load payment gateway. Please refresh the page.');
    };
    document.body.appendChild(script);

    // Fetch payment options from backend
    fetchPaymentOptions();

    // Check if logged-in user is an approved member (25% event discount)
    const token = localStorage.getItem('access_token');
    if (token) {
      axios.get(`${API_BASE_URL}/api/events/user/member-status`, { headers: { Authorization: `Bearer ${token}` } })
        .then(({ data }) => setIsMember(!!data.is_member))
        .catch(() => {});
    }

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const fetchPaymentOptions = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/flexible-payment/payment-options`);
      setPaymentOptions(data);
      setOptionsLoading(false);
    } catch (err) {
      console.error('Failed to fetch payment options:', err);
      setOptionsLoading(false);
    }
  };

  // Build dynamic category options
  const getCategories = () => {
    if (!paymentOptions) return {};

    const categories = {};

    // Membership - only show registration fee for first 1000 members
    if (paymentOptions.membership) {
      const membership = paymentOptions.membership;
      const showAnnualFee = membership.individual.annual_fee_active;
      
      categories.membership = {
        name: 'Membership Registration',
        hasSubcategory: true,
        subcategories: [
          {
            value: 'individual',
            label: 'Individual Professional',
            registration_fee: membership.individual.registration_fee,
            annual_fee: showAnnualFee ? membership.individual.annual_fee : 0,
            amount: showAnnualFee
              ? membership.individual.registration_fee + membership.individual.annual_fee
              : membership.individual.registration_fee
          },
          ...membership.institutional.tiers.map(tier => ({
            value: `institutional_tier${tier.tier}`,
            label: `Institutional ${tier.label}`,
            registration_fee: tier.registration_fee,
            annual_fee: showAnnualFee ? tier.annual_fee : 0,
            amount: showAnnualFee
              ? tier.registration_fee + tier.annual_fee
              : tier.registration_fee
          }))
        ]
      };
    }

    // Events - always show, even if no events available
    if (paymentOptions.events && paymentOptions.events.length > 0) {
      categories.event = {
        name: 'Events',
        hasSubcategory: true,
        subcategories: paymentOptions.events.map(event => ({
          value: event.id,
          label: `${event.title} — ₹${(event.fee / 100).toLocaleString('en-IN')}${event.member_fee ? ` (Members ₹${(event.member_fee / 100).toLocaleString('en-IN')})` : ''}`,
          amount: (isMember && event.member_fee) ? event.member_fee : event.fee
        }))
      };
    } else {
      categories.event = {
        name: 'Events',
        hasSubcategory: true,
        subcategories: [
          { value: 'no_events', label: 'No upcoming events available', amount: null, disabled: true }
        ]
      };
    }

    // Research Papers are now a members-only benefit — not sold individually

    // Donation - always show
    categories.donation = { name: 'Donation', hasSubcategory: false };
    
    // Others - always show
    categories.other = { name: 'Others', hasSubcategory: false };

    return categories;
  };

  const categories = getCategories();

  const handleCategoryChange = (e) => {
    setSelectedMembershipFees(null); // Clear fee breakdown
    setPaymentData({
      ...paymentData,
      category: e.target.value,
      subcategory: '',
      customAmount: ''
    });
  };

  const [searchParams] = useSearchParams();
  const prefillApplied = React.useRef(false);

  useEffect(() => {
    if (!paymentOptions || prefillApplied.current) return;
    const cat = searchParams.get('category');
    if (!cat) return;
    prefillApplied.current = true;
    const item = searchParams.get('item');
    const cats = getCategories();
    if (!cats[cat]) return;
    const sub = item ? cats[cat].subcategories?.find(s => s.value === item) : null;
    setPaymentData(prev => ({
      ...prev,
      category: cat,
      subcategory: sub ? sub.value : '',
      customAmount: sub?.amount ? String(sub.amount / 100) : prev.customAmount
    }));
    if (cat === 'membership' && sub) {
      setSelectedMembershipFees({
        registration_fee: sub.registration_fee / 100,
        annual_fee: sub.annual_fee / 100
      });
    }
  }, [paymentOptions]);

  // Keep event amount in sync with member discount status
  useEffect(() => {
    if (paymentData.category !== 'event' || !paymentData.subcategory || !paymentOptions) return;
    const ev = paymentOptions.events?.find(e => e.id === paymentData.subcategory);
    if (ev) {
      const amt = (isMember && ev.member_fee) ? ev.member_fee : ev.fee;
      setPaymentData(prev => ({ ...prev, customAmount: String(amt / 100) }));
    }
  }, [isMember, paymentData.subcategory, paymentData.category, paymentOptions]);

  const handleSubcategoryChange = (e) => {
    const selectedSubcat = categories[paymentData.category].subcategories.find(
      sub => sub.value === e.target.value
    );
    
    // Convert amount from paise to rupees for display (divide by 100)
    const amountInRupees = selectedSubcat?.amount ? selectedSubcat.amount / 100 : '';
    
    // Store membership fee breakdown
    if (paymentData.category === 'membership' && selectedSubcat) {
      setSelectedMembershipFees({
        registration_fee: selectedSubcat.registration_fee / 100,
        annual_fee: selectedSubcat.annual_fee / 100
      });
    } else {
      setSelectedMembershipFees(null);
    }
    
    setPaymentData({
      ...paymentData,
      subcategory: e.target.value,
      customAmount: amountInRupees
    });
  };

  const selectedEventOpt = paymentData.category === 'event' && paymentOptions?.events?.find(e => e.id === paymentData.subcategory);
  const earlyBirdBlocked = !!(selectedEventOpt && selectedEventOpt.early_bird_until &&
    new Date(selectedEventOpt.early_bird_until) > new Date() && !isMember);

  const handlePayment = async (e) => {
    e.preventDefault();
    if (earlyBirdBlocked) return;
    
    // Check if Razorpay script is loaded
    if (!razorpayLoaded || !window.Razorpay) {
      setError('Payment gateway is still loading. Please wait a moment and try again.');
      return;
    }

    // Check if Razorpay Key is configured
    if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
      setError('Payment gateway is not configured. Please contact support.');
      console.error('REACT_APP_RAZORPAY_KEY_ID is not set');
      return;
    }
    
    // Validation
    if (!paymentData.name || !paymentData.email || !paymentData.phone) {
      setError('Please fill in all contact details');
      return;
    }

    const amount = parseInt(paymentData.customAmount);
    if (!amount || amount < 1) {
      setError('Please enter a valid amount (minimum ₹1)');
      return;
    }

    if (amount < 10 && paymentData.category !== 'testing') {
      setError('Minimum amount is ₹10 for non-test payments');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create Razorpay order for flexible payment
      const { data: orderData } = await axios.post(
        `${API_BASE_URL}/api/flexible-payment/create-order`,
        {
          amount: amount * 100, // Convert to paise
          currency: 'INR',
          category: paymentData.category,
          subcategory: paymentData.subcategory,
          purpose: paymentData.purpose,
          customer_name: paymentData.name,
          customer_email: paymentData.email,
          customer_phone: paymentData.phone
        }
      );

      // Initialize Razorpay
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: 'INR',
        order_id: orderData.id,
        name: 'CSED',
        description: `${categories[paymentData.category].name} - ${paymentData.purpose}`,
        image: '/logo.png',
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(`${API_BASE_URL}/api/flexible-payment/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            // Success
            navigate('/payment/success', {
              state: { 
                paymentId: response.razorpay_payment_id,
                amount: amount,
                category: paymentData.category,
                purpose: paymentData.purpose
              }
            });
          } catch (err) {
            setError('Payment verification failed. Please contact support.');
            setLoading(false);
          }
        },
        prefill: {
          name: paymentData.name,
          email: paymentData.email,
          contact: paymentData.phone
        },
        theme: {
          color: '#E87722'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      if (!window.Razorpay) {
        setError('Payment gateway failed to initialize. Please refresh the page.');
        setLoading(false);
        return;
      }

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const currentCategory = categories[paymentData.category];
  const showSubcategory = currentCategory?.hasSubcategory;

  // Show loading state while fetching options
  if (optionsLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-32 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#E87722] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment options...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#1E3A5F]/90 text-white py-16 pt-32">
        <div className="container mx-auto px-4 text-center">
          <CreditCard className="w-16 h-16 text-[#E87722] mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4">CSED Payment Gateway</h1>
          <p className="text-gray-200 max-w-2xl mx-auto">
            Make payments for memberships, events, donations, and more
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {error && (
          <div className="max-w-2xl mx-auto mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl text-[#1E3A5F]">Payment Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePayment} className="space-y-6">
              {/* Payment Category */}
              <div>
                <Label htmlFor="category">Payment Category *</Label>
                <select
                  id="category"
                  value={paymentData.category}
                  onChange={handleCategoryChange}
                  className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                  required
                >
                  {Object.entries(categories).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Subcategory (conditional) */}
              {showSubcategory && (
                <div>
                  <Label htmlFor="subcategory">Select Option *</Label>
                  <select
                    id="subcategory"
                    value={paymentData.subcategory}
                    onChange={handleSubcategoryChange}
                    className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">-- Select --</option>
                    {currentCategory.subcategories.map(sub => (
                      <option key={sub.value} value={sub.value}>{sub.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Membership Fee Breakdown */}
              {selectedMembershipFees && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-[#1E3A5F] mb-2">Fee Breakdown</h3>
                  
                  {/* Registration Fee */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700">Registration Fee</Label>
                    </div>
                    <div>
                      <Input
                        type="text"
                        value={`₹${selectedMembershipFees.registration_fee.toLocaleString('en-IN')}`}
                        readOnly
                        className="bg-white cursor-not-allowed font-semibold text-[#1E3A5F]"
                      />
                    </div>
                  </div>

                  {/* Annual Fee */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700">Annual Fee</Label>
                    </div>
                    <div>
                      <Input
                        type="text"
                        value={selectedMembershipFees.annual_fee === 0 ? '₹0' : `₹${selectedMembershipFees.annual_fee.toLocaleString('en-IN')}`}
                        readOnly
                        className="bg-white cursor-not-allowed font-semibold text-[#1E3A5F]"
                      />
                    </div>
                  </div>

                  {selectedMembershipFees.annual_fee === 0 && (
                    <p className="text-sm text-blue-600 mt-2">
                      ℹ️ Annual fee waived for the first 1000 members
                    </p>
                  )}
                </div>
              )}

              {/* Amount */}
              <div>
                <Label htmlFor="amount">Amount (₹) *</Label>
                <Input
                  id="amount"
                  type="number"
                  value={paymentData.customAmount}
                  onChange={(e) => setPaymentData({...paymentData, customAmount: e.target.value})}
                  placeholder="Enter amount in rupees"
                  required
                  min="1"
                  readOnly={paymentData.category === 'membership' || paymentData.category === 'event'}
                  className={(paymentData.category === 'membership' || paymentData.category === 'event') ? 'bg-gray-100 cursor-not-allowed' : ''}
                />
                {paymentData.category === 'membership' && paymentData.customAmount && (
                  <p className="text-sm text-blue-600 mt-1">
                    ℹ️ Registration fee is fixed based on membership type selected
                  </p>
                )}
                {paymentData.category === 'event' && paymentData.subcategory && paymentData.subcategory !== 'no_events' && (
                  isMember ? (
                    <p className="text-sm text-green-600 mt-1" data-testid="member-discount-applied">
                      ✓ 25% CSED Member discount applied
                    </p>
                  ) : (
                    <p className="text-sm text-blue-600 mt-1" data-testid="member-discount-pitch">
                      CSED Members pay 25% less on event registration.{' '}
                      <a href="/membership" className="underline text-[#E87722] font-medium">Become a Member</a>
                    </p>
                  )
                )}
                {earlyBirdBlocked && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="early-bird-blocked-notice">
                    <p className="text-sm text-amber-800 font-medium">
                      Members-only early access is on for this event.
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      Public registration opens on {new Date(selectedEventOpt.early_bird_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}.{' '}
                      <a href="/membership" className="underline text-[#E87722] font-semibold">Become a Member</a> for instant access and 25% off.
                    </p>
                  </div>
                )}
              </div>

              {/* Purpose */}
              <div>
                <Label htmlFor="purpose">Purpose / Description *</Label>
                <Input
                  id="purpose"
                  type="text"
                  value={paymentData.purpose}
                  onChange={(e) => setPaymentData({...paymentData, purpose: e.target.value})}
                  placeholder="e.g., Test payment, Event registration, etc."
                  required
                />
              </div>

              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-[#1E3A5F] mb-4">Contact Information</h3>
                
                {/* Name */}
                <div className="mb-4">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    value={paymentData.name}
                    onChange={(e) => setPaymentData({...paymentData, name: e.target.value})}
                    placeholder="Your full name"
                    required
                  />
                </div>

                {/* Email */}
                <div className="mb-4">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={paymentData.email}
                    onChange={(e) => setPaymentData({...paymentData, email: e.target.value})}
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                {/* Phone */}
                <div className="mb-4">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={paymentData.phone}
                    onChange={(e) => setPaymentData({...paymentData, phone: e.target.value})}
                    placeholder="+91 9876543210"
                    required
                  />
                </div>
              </div>

              {/* Amount Summary */}
              {paymentData.customAmount && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-[#1E3A5F]">Total Amount:</span>
                    <span className="text-2xl font-bold text-[#E87722]">
                      ₹{parseInt(paymentData.customAmount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || earlyBirdBlocked}
                className="w-full bg-[#E87722] hover:bg-[#d66a1a] text-white py-6 text-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Proceed to Payment
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
